import {get, values, mapValues, cloneDeep, transform, find, isPlainObject} from 'lodash';

import {ProbeProcessor, ProcessorDefinition, StageType, StageValueSchema} from '../types';
import {PROCESSOR_DYNAMIC_DATA_TYPES, PROCESSOR_STATIC_DATA_TYPES} from '../consts';
import humanizeString from '../../humanizeString';

export const transformOldServiceSchema = (serviceItems) => {
  const serviceItemsValueType = serviceItems?.application_schema?.properties?.value?.type;
  if (!serviceItemsValueType || serviceItemsValueType === 'object') return serviceItems;
  const result = cloneDeep(serviceItems);
  result.application_schema.properties.value = {
    type: 'object',
    properties: {
      value: {
        type: serviceItemsValueType
      }
    },
  };
  return result;
};

export const buildProcessorStageValuesFromServiceValues = (
  serviceValues?: {properties: any},
  valueMap?: Record<string, Record<number, string>>
) => {
  function serviceValuesToStageValueSchema(value: any, name: string): StageValueSchema[] {
    const result = {
      name,
      title: humanizeString(name),
      type: value.type === 'integer' ? 's64' : value.type
    };
    if (valueMap?.[name]) {
      return [{
        ...result,
        type: 'string',
        possible_values: values(valueMap[name])
      }];
    } else {
      return [result];
    }
  }
  return mapValues(serviceValues?.properties, serviceValuesToStageValueSchema);
};

export const buildProcessorStageKeysFromServiceKeys = (serviceKeys?: {properties: any}) => {
  return transform(serviceKeys?.properties, (result, {type}, name) => {
    result[name] = type;
  }, {});
};

export const enhanceProcessorDefinitionWithServiceProperties = (
  processorDefinition: ProcessorDefinition, processor: ProbeProcessor, telemetryServiceRegistryItems: any[]
) => {
  const {service_name: serviceName, value_map: valueMap, data_type: dataType} = processor.properties;
  const service = serviceName ?
    transformOldServiceSchema(find(telemetryServiceRegistryItems, {service_name: serviceName})) : null;
  const serviceProperties = service?.application_schema?.properties;
  if (isPlainObject(serviceProperties)) {
    const {value: serviceValues, key: serviceKeys} = serviceProperties;
    const isDynamicProcessor = dataType === 'dynamic';
    processorDefinition = cloneDeep(processorDefinition);
    processorDefinition.outputs.out.types = transform(
      processorDefinition.outputs.out.types,
      (result, outputType) => {
        if (outputType.dynamic !== isDynamicProcessor) return;
        outputType.values = buildProcessorStageValuesFromServiceValues(serviceValues, valueMap);
        outputType.keys = buildProcessorStageKeysFromServiceKeys(serviceKeys);
        result.push(outputType);
      },
      [] as StageType[]
    );
  }
  return processorDefinition;
};

export const isHiddenProperty = (propertyName: string, processor: ProbeProcessor) => {
  if (processor.type !== 'extensible_data_collector') return false;
  const dataType = get(processor, ['properties', 'data_type']);
  return (
    (propertyName === 'keys' && PROCESSOR_DYNAMIC_DATA_TYPES.has(dataType)) ||
    (propertyName === 'ingestion_filter' && PROCESSOR_STATIC_DATA_TYPES.has(dataType))
  );
};
